import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Typography,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../helpers/Loader";
import WarnModal from "../../components/modals/WarnModal";
import Pagination from "../../components/Pagination";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validation";
import { errorToast, successToast } from "../../helpers";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  useAddSubjectMutation,
  useDeleteByIdSubjectMutation,
  useLazyGetAllSubjectQuery,
  useUpdateSubjectMutation,
} from "../../services/category";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import moment from "moment";

export interface Subject {
  _id: string;
  name: string;
  status: boolean;
  createdAt: string;
}

const Subjecta = () => {
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  let totalPages = Math.ceil(totalCount / 10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const navigate = useNavigate();
  const { _id } = useParams();
  const [openModal, setOpenModal] = useState(false);
  const [subjectInput, setSubjectInput] = useState("");
  const [inputError, setInputError] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleClose = () => {
    setOpenModal(false);
    setSubjectInput("");
    setEditingId(null);
  };

  const [rows, setRows] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [deleteSubject] = useDeleteByIdSubjectMutation();
  const [getAllSubject] = useLazyGetAllSubjectQuery();
  const [updateSubject] = useUpdateSubjectMutation();
  const [addSubject] = useAddSubjectMutation();

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const fetchSubjects = async () => {
    try {
      setIsLoading(true);
      const response = await getAllSubject({
        page: page,
        categoryId: _id,
        search: debouncedSearchTerm,
      }).unwrap();
      if (response.statusCode === 200) {
        setRows(response.data?.data);
        setTotalCount(response?.data?.total);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: any) => {
    try {
      const response = await deleteSubject({ id }).unwrap();
      if (response?.statusCode === 200) {
        successToast("Subject deleted successfully");
        fetchSubjects();
      }
    } catch (error: any) {
      console.error(error);
      errorToast(error?.message || "");
    }
  };

  const changeStatus = async (id: any, status: any) => {
    try {
      const body = {
        status: status,
      };
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

      const response = await updateSubject({
        body: encryptedBody,
        id,
      }).unwrap();
      if (response?.statusCode === 200) {
        successToast("Subject status updated successfully");
        fetchSubjects();
      }
    } catch (error: any) {
      errorToast(error?.message);
      console.error(error);
    }
  };

  const handleSaveSubject = async () => {
    if (subjectInput.trim().length === 0 || subjectInput.length > 100) {
      setInputError(true);
      return;
    }
    const body = {
      name: subjectInput,
      categoryId: _id,
    };

    if (editingId) {
      // Update existing subject
      try {
        setIsLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response: any = await updateSubject({
          body: encryptedBody,
          id: editingId,
        }).unwrap();
        if (response?.statusCode === 200) {
          successToast("Subject updated successfully");
          fetchSubjects();
        }
      } catch (error: any) {
        console.error("Error update:", error);
        errorToast(error?.data?.message || "");
      } finally {
        setIsLoading(false);
      }
    } else {
      // Add new subject
      try {
        setIsLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response: any = await addSubject({
          body: encryptedBody,
        }).unwrap();
        if (response?.statusCode === 200) {
          successToast("Subject added successfully");
          fetchSubjects();
          // successToast("Forum updated successfully");
          // navigate("/manage-forum");
        }
      } catch (error: any) {
        console.error("Error adding:", error);
        errorToast(error?.data?.message || "");
      } finally {
        setIsLoading(false);
      }
    }

    handleClose();
  };

  const handleEditClick = (id: string) => {
    const subjectToEdit = rows.find((row) => row._id === id);
    if (subjectToEdit) {
      setSubjectInput(subjectToEdit.name);
      setEditingId(id);
      setOpenModal(true);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, [page, debouncedSearchTerm]);

  return (
    <div>
      <Loader isLoad={isLoading} />
      <Box className="cards_header">
        <SearchBar
          value={searchTerm}
          searchTerm={searchTerm}
          onCross={() => setSearchTerm("")}
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          onChange={(val: any) => {
            if (isValidInput(val.target.value)) {
              setSearchTerm(val.target.value);
            }
          }}
        />

        <Box className="cards_header_right">
          <Button
            className="btn btn_primary"
            onClick={() => setOpenModal(true)}
          >
            <AddIcon />
            Add Subject
          </Button>
        </Box>
      </Box>
      <TableContainer className="table_container">
        <Box className="heading"></Box>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">S.No</TableCell>
              <TableCell align="center">Subject Name</TableCell>
              <TableCell align="center">Created At</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No Subjects found
                </TableCell>
              </TableRow>
            ) : (
              rows?.map((row: Subject, i: number) => (
                <TableRow key={row._id}>
                  <TableCell align="center">
                    {(page - 1) * 10 + i + 1}
                  </TableCell>
                  <TableCell>{row?.name}</TableCell>
                  <TableCell>{moment(row.createdAt).format(" lll ")}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={row?.status}
                      onChange={() => changeStatus(row?._id, !row?.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box className="table_actions">
                      {/* <IconButton
                        onClick={() =>
                          navigate(`/manage-subjects/details/${row._id}`)
                        }
                      >
                        <VisibilityIcon />
                      </IconButton> */}
                      <IconButton onClick={() => handleEditClick(row._id)}>
                        <ModeEditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setOpen(true);
                          setSelectedId(row._id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows?.length > 0 ? (
        <Pagination
          setPage={setPage}
          module={rows}
          page={page}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      ) : (
        ""
      )}
      <Dialog open={openModal} onClose={handleClose} className="revert_dialog">
        <DialogTitle>{editingId ? "Edit Subject" : "Add Subject"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="subject-input"
            label="Subject Name"
            type="text"
            fullWidth
            value={subjectInput}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (inputValue.length <= 100) {
                setSubjectInput(inputValue);
                setInputError(false);
              } else {
                setInputError(true);
              }
            }}
            error={inputError}
            helperText={
              inputError
                ? "Subject name can't be more than 100 characters."
                : ""
            }
          />
        </DialogContent>

        <DialogActions>
          <Button sx={{ background: "#65a442" }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            sx={{ background: "#65a442" }}
            onClick={handleSaveSubject}
            disabled={inputError || subjectInput.trim().length === 0}
          >
            {editingId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => {
          handleDelete(selectedId);
        }}
        name="Subject"
      />
    </div>
  );
};

export default Subjecta;
